<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import kanbanAPI from 'dashboard/api/kanban';
import { toIntlLocale } from 'shared/helpers/intlLocale';

const props = defineProps({ item: { type: Object, required: true } });
const { t, locale } = useI18n();
const expanded = ref(false);
const loading = ref(false);
const failed = ref(false);
const report = ref(null);
let requestVersion = 0;
const rows = computed(() =>
  Object.entries(report.value?.stage_breakdown || {})
);
const formatDate = value => {
  if (!value) return t('KANBAN.HISTORY.STAGE_TIME.UNKNOWN');
  return new Intl.DateTimeFormat(toIntlLocale(locale.value), {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};
const stageName = stage => {
  const stages =
    props.item.funnel?.stages || props.item.item_details?.funnel?.stages;
  return stages?.[stage]?.name || stage;
};
const load = async () => {
  requestVersion += 1;
  const version = requestVersion;
  report.value = null;
  failed.value = false;
  if (!expanded.value || !props.item.id) {
    loading.value = false;
    return;
  }
  loading.value = true;
  try {
    const { data } = await kanbanAPI.getStageTimeBreakdown(props.item.id);
    if (version === requestVersion) report.value = data;
  } catch {
    if (version === requestVersion) failed.value = true;
  } finally {
    if (version === requestVersion) loading.value = false;
  }
};
watch(
  () => [
    expanded.value,
    props.item.id,
    props.item.activities,
    props.item.funnel_stage,
    props.item.item_details?.status,
  ],
  load
);
</script>

<template>
  <section class="mb-5 border-b border-n-weak pb-4">
    <button
      type="button"
      class="flex min-h-10 w-full items-center justify-between gap-3 rounded-xl px-2 text-left text-sm font-semibold text-n-slate-12 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      {{ t('KANBAN.HISTORY.STAGE_TIME.TITLE') }}
      <span
        :class="expanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        class="size-4 shrink-0"
        aria-hidden="true"
      />
    </button>
    <div v-if="expanded" class="mt-3" :aria-busy="loading">
      <p v-if="loading" role="status" class="text-sm text-n-slate-11">
        {{ t('KANBAN.HISTORY.STAGE_TIME.LOADING') }}
      </p>
      <div
        v-else-if="failed"
        role="alert"
        class="flex flex-wrap items-center gap-3 text-sm"
      >
        <span>{{ t('KANBAN.HISTORY.STAGE_TIME.ERROR') }}</span>
        <button
          type="button"
          class="rounded-xl px-3 py-2 font-semibold text-n-blue-text hover:bg-n-alpha-2"
          @click="load"
        >
          {{ t('KANBAN.HISTORY.STAGE_TIME.RETRY') }}
        </button>
      </div>
      <template v-else-if="rows.length">
        <p class="mb-3 text-xs text-n-slate-11">
          {{
            t(
              report.completed
                ? 'KANBAN.HISTORY.STAGE_TIME.CLOSED'
                : 'KANBAN.HISTORY.STAGE_TIME.LIVE'
            )
          }}
        </p>
        <p
          v-if="report.history_complete === false"
          class="mb-3 text-xs text-n-amber-11"
        >
          {{ t('KANBAN.HISTORY.STAGE_TIME.INCOMPLETE') }}
        </p>
        <ol class="m-0 list-none divide-y divide-n-weak p-0">
          <li
            v-for="[key, row] in rows"
            :key="key"
            class="grid gap-2 py-3 sm:grid-cols-[minmax(0,1fr)_auto]"
          >
            <div class="min-w-0">
              <p class="m-0 text-sm font-semibold text-n-slate-12">
                {{ stageName(row.stage) }}
              </p>
              <dl
                class="m-0 mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-n-slate-11"
              >
                <div>
                  <dt class="inline">
                    {{ t('KANBAN.HISTORY.STAGE_TIME.ENTERED') }}:
                  </dt>
                  <dd class="m-0 inline">{{ formatDate(row.entered_at) }}</dd>
                </div>
                <div>
                  <dt class="inline">
                    {{ t('KANBAN.HISTORY.STAGE_TIME.EXITED') }}:
                  </dt>
                  <dd class="m-0 inline">
                    {{
                      row.exited_at
                        ? formatDate(row.exited_at)
                        : t(
                            report.completed
                              ? 'KANBAN.HISTORY.STAGE_TIME.UNKNOWN'
                              : 'KANBAN.HISTORY.STAGE_TIME.CURRENT'
                          )
                    }}
                  </dd>
                </div>
              </dl>
            </div>
            <p class="m-0 text-sm tabular-nums text-n-slate-12">
              {{
                row.duration_seconds == null
                  ? t('KANBAN.HISTORY.STAGE_TIME.UNKNOWN')
                  : row.duration_formatted
              }}
            </p>
          </li>
        </ol>
      </template>
      <p v-else class="text-sm text-n-slate-11">
        {{ t('KANBAN.HISTORY.NO_ACTIVITY') }}
      </p>
    </div>
  </section>
</template>
